/**
 * The drawing surface.
 *
 * A `<canvas>` sitting exactly over the chart, sharing its coordinate space.
 * Lightweight Charts owns the candles; this owns everything the user drew.
 *
 * **Why a sibling canvas rather than a chart primitive.** Lightweight Charts'
 * primitives API renders inside the chart's own pane, which is right for
 * annotations that must clip to the price scale — and wrong for interaction.
 * Drag handles need pointer capture, hover cursors, and a hit-test that runs
 * before the chart's own pan handler sees the event. A sibling canvas gets all
 * three from the DOM for free, and the coordinate transform is shared through
 * `timeToCoordinate`/`priceToCoordinate`, so the two layers cannot drift.
 *
 * **All geometry lives in `@aegis/core`.** This file converts events to points,
 * asks the model what was hit, and paints. It contains no distance maths, which
 * is why the awkward cases — clicking past the end of a trendline, an anchor on
 * an older drawing beating a body hit on a newer one — are unit-tested without
 * a browser.
 *
 * **User drawings are a different hue from AI annotations.** `--user-drawing-*`
 * against `--ai-primary`. That separation is a stated invariant, not a
 * preference: a user must never mistake the machine's line for their own.
 */
import { chart as chartTokens } from '@aegis/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX, PointerEvent as ReactPointerEvent } from 'react';
import {
  anchorCount, fibLevels, hitTest, measure, moveAnchor, normalise, project, translate,
  unproject,
  type Anchor, type Drawing, type DrawingKind, type Point, type Viewport,
} from '@aegis/core';
import { metric, onThemeChange, token } from '../lib/theme.js';

export type Tool = 'cursor' | DrawingKind;

export interface DrawingLayerProps {
  /** Live view of the chart. Recreated on every pan, zoom and resize. */
  viewport: Viewport | null;
  drawings: Drawing[];
  tool: Tool;
  symbol: string;
  /** Seconds per bar, for the measure tool's bar count. */
  barSeconds: number;
  selectedId: string | null;
  onSelect(id: string | null): void;
  /** Called with a complete drawing — on creation and after every edit. */
  onCommit(d: Drawing): void;
  /** Creation finished, so the toolbar can drop back to the cursor. */
  onToolDone(): void;
}

const HANDLE_RADIUS = 4;

interface DragState {
  readonly id: string;
  /** Which anchor is moving, or null when the whole drawing is. */
  readonly anchorIndex: number | null;
  readonly startAnchor: Anchor;
  readonly original: Drawing;
}

export function DrawingLayer({
  viewport, drawings, tool, symbol, barSeconds, selectedId,
  onSelect, onCommit, onToolDone,
}: DrawingLayerProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pending, setPending] = useState<Drawing | null>(null);
  const [cursor, setCursor] = useState<Point | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [, forceRepaint] = useState(0);

  const toPoint = useCallback((e: ReactPointerEvent<HTMLCanvasElement>): Point => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  // --- paint ----------------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !viewport) return;

    // Backing store at device resolution; a 1x canvas on a retina display makes
    // a one-pixel line look like a smudge next to the chart's crisp candles.
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== viewport.width * dpr || canvas.height !== viewport.height * dpr) {
      canvas.width = viewport.width * dpr;
      canvas.height = viewport.height * dpr;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, viewport.width, viewport.height);

    const all = pending ? [...drawings, pending] : drawings;
    for (const d of all) paint(ctx, d, viewport, d.id === selectedId, barSeconds);
  });

  // --- interaction ----------------------------------------------------------

  function onPointerDown(e: ReactPointerEvent<HTMLCanvasElement>): void {
    if (!viewport) return;
    const p = toPoint(e);
    const anchor = unproject(p, viewport);
    if (!anchor) return;

    // --- creating ---
    if (tool !== 'cursor') {
      const needed = anchorCount(tool);
      if (!pending) {
        const draft: Drawing = {
          id: `dr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
          kind: tool, symbol, anchors: [anchor], createdAt: Date.now(),
        };
        // A one-anchor drawing is complete the moment it is placed.
        if (needed === 1) { onCommit(normalise(draft)); onToolDone(); }
        else setPending(draft);
      }
      return;
    }

    // --- selecting and dragging ---
    const hit = hitTest(p, drawings, viewport);
    onSelect(hit?.drawing.id ?? null);
    if (!hit || hit.drawing.locked) return;

    dragRef.current = {
      id: hit.drawing.id,
      anchorIndex: hit.anchorIndex,
      startAnchor: anchor,
      original: hit.drawing,
    };
    // Pointer capture, so a fast drag that leaves the canvas keeps tracking
    // instead of stranding the drawing mid-move.
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: ReactPointerEvent<HTMLCanvasElement>): void {
    if (!viewport) return;
    const p = toPoint(e);
    setCursor(p);

    const drag = dragRef.current;
    if (drag) {
      const now = unproject(p, viewport);
      if (!now) return;
      const next = drag.anchorIndex === null
        ? translate(drag.original, now.time - drag.startAnchor.time, now.price - drag.startAnchor.price)
        : moveAnchor(drag.original, drag.anchorIndex, now);
      onCommit(next);
      return;
    }

    // Rubber-banding the second anchor of an in-progress drawing.
    if (pending) {
      const now = unproject(p, viewport);
      if (now) setPending({ ...pending, anchors: [pending.anchors[0]!, now] });
      return;
    }

    forceRepaint((n) => n + 1);
  }

  function onPointerUp(e: ReactPointerEvent<HTMLCanvasElement>): void {
    if (dragRef.current) {
      // Normalise once, at the end. Doing it during the drag would flip the
      // anchors under the user's cursor as they crossed the other end.
      const finished = drawings.find((d) => d.id === dragRef.current!.id);
      if (finished) onCommit(normalise(finished));
      dragRef.current = null;
      e.currentTarget.releasePointerCapture(e.pointerId);
      return;
    }

    if (pending && pending.anchors.length === 2) {
      onCommit(normalise(pending));
      setPending(null);
      onToolDone();
    }
  }

  // A theme change is not a React render, so the canvas would keep whatever it
  // last painted. Same class of bug as the chart's — canvas does not follow CSS.
  useEffect(() => onThemeChange(() => forceRepaint((n) => n + 1)), []);

  // Escape abandons an in-progress drawing rather than leaving a half-line
  // following the cursor with no way out but drawing it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') { setPending(null); onToolDone(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onToolDone]);

  const hovering = viewport && cursor && tool === 'cursor'
    ? hitTest(cursor, drawings, viewport) : null;

  return (
    <canvas
      ref={canvasRef}
      className="drawing-layer"
      style={{
        width: viewport?.width ?? 0,
        height: viewport?.height ?? 0,
        // `none` lets every chart gesture through untouched when no tool is
        // active and nothing is under the cursor — panning and zooming must not
        // get worse because a drawing layer exists.
        pointerEvents: tool === 'cursor' && !hovering && !dragRef.current ? 'none' : 'auto',
        cursor: tool !== 'cursor' ? 'crosshair' : hovering ? 'move' : 'default',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={() => setCursor(null)}
    />
  );
}

// ---------------------------------------------------------------------------
// Painting
// ---------------------------------------------------------------------------

function paint(
  ctx: CanvasRenderingContext2D,
  d: Drawing,
  v: Viewport,
  selected: boolean,
  barSeconds: number,
): void {
  const pts = d.anchors.map((a) => project(a, v));
  if (pts.some((p) => p === null)) return;
  const [a, b] = pts as [Point, Point?];

  const stroke = token('userDrawing');
  const dim = token('textMuted');

  ctx.save();
  ctx.strokeStyle = stroke;
  ctx.fillStyle = stroke;
  ctx.lineWidth = selected ? chartTokens.drawing.selectedLineWidthPx : chartTokens.drawing.lineWidthPx;
  ctx.font = `11px ${metric('--font-mono', 'monospace')}`;

  switch (d.kind) {
    case 'horizontal':
      line(ctx, { x: 0, y: a.y }, { x: v.width, y: a.y });
      break;

    case 'vertical':
      line(ctx, { x: a.x, y: 0 }, { x: a.x, y: v.height });
      break;

    case 'trendline':
      if (b) line(ctx, a, b);
      break;

    case 'ray':
      if (b) {
        // Extend to the far edge rather than to a fixed length, so the ray
        // still reaches the edge after a zoom out.
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const scale = dx === 0 ? v.height : Math.max(v.width, v.height) / Math.hypot(dx, dy);
        line(ctx, a, { x: a.x + dx * scale, y: a.y + dy * scale });
      }
      break;

    case 'rectangle':
      if (b) {
        ctx.globalAlpha = chartTokens.drawing.zoneOpacity;
        ctx.fillRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
        ctx.globalAlpha = 1;
        ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
      }
      break;

    case 'fib':
      if (b) {
        const left = Math.min(a.x, b.x);
        const right = Math.max(a.x, b.x);
        for (const { ratio, price } of fibLevels(d.anchors[0]!, d.anchors[1]!)) {
          const y = v.priceToY(price);
          if (y === null) continue;
          ctx.globalAlpha = ratio === 0 || ratio === 1 ? 1 : chartTokens.drawing.fibIntermediateOpacity;
          line(ctx, { x: left, y }, { x: right, y });
          ctx.globalAlpha = 1;
          ctx.fillStyle = dim;
          ctx.fillText(`${(ratio * 100).toFixed(1)}%  ${price.toFixed(2)}`, right + 6, y + 3);
          ctx.fillStyle = stroke;
        }
      }
      break;

    case 'measure':
      if (b) {
        const m = measure(d.anchors[0]!, d.anchors[1]!, barSeconds);
        ctx.setLineDash([...chartTokens.drawing.dash]);
        line(ctx, a, b);
        ctx.setLineDash([]);
        // Descriptive only: a distance and a duration, never a judgement about
        // what the move means or what to do next.
        const label = `${m.priceChange >= 0 ? '+' : ''}${m.priceChange.toFixed(2)} ` +
          `(${m.percentChange >= 0 ? '+' : ''}${m.percentChange.toFixed(2)}%) · ${m.bars} bars`;
        boxedText(ctx, label, (a.x + b.x) / 2, Math.min(a.y, b.y) - 10);
      }
      break;

    case 'text':
      boxedText(ctx, d.text || 'Note', a.x, a.y);
      break;
  }

  if (selected) for (const p of pts as Point[]) handle(ctx, p, stroke);
  ctx.restore();
}

function line(ctx: CanvasRenderingContext2D, a: Point, b: Point): void {
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
}

function handle(ctx: CanvasRenderingContext2D, p: Point, stroke: string): void {
  ctx.save();
  // Filled with the surface colour and ringed, so a handle stays visible on top
  // of the line it sits on rather than disappearing into it.
  ctx.fillStyle = token('surfaceBase');
  ctx.strokeStyle = stroke;
  ctx.lineWidth = chartTokens.drawing.selectedLineWidthPx;
  ctx.beginPath();
  ctx.arc(p.x, p.y, HANDLE_RADIUS, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function boxedText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number): void {
  const padding = 4;
  const width = ctx.measureText(text).width;
  ctx.save();
  ctx.globalAlpha = 0.9;
  ctx.fillStyle = token('surfaceOverlay');
  ctx.fillRect(x - width / 2 - padding, y - 12, width + padding * 2, 16);
  ctx.globalAlpha = 1;
  ctx.fillStyle = token('userDrawing');
  ctx.textAlign = 'center';
  ctx.fillText(text, x, y);
  ctx.restore();
}
